/*
 * A Google map of visits: one numbered pin per visit in time order, in the first crew member's color (gray with no crew,
 * green when done). Hover a pin for the client and time, click it to open the visit. The pin for `active` (a row hovered in
 * the list next to it) grows so the two line up. A visit whose address has no map spot yet is looked up by the server once
 * (POST map-spots, saved on the property); one Google can't find is left off and counted under the map. The browser's own
 * geocoder is not used here: our map key does not have the Geocoding API (2026-10-30).
 *
 * Drag to move, the + and - buttons or Ctrl and scroll to zoom (two fingers on a phone), so the page still scrolls past it.
 * The key is HQ's Google Maps browser key (Settings, Connected services). Leaflet's free tiles showed nothing for us
 * (2026-10-29).
 *
 * The Route view passes its own numbers and colors (each route's stops numbered in driving order, in the route's color) and
 * a line per route through its stops.
 *
 * With `live`, a blue dot shows where this browser is (asks once for the location) and a green pin with initials and name
 * shows each person on the clock whose phone sent a spot in the last 30 minutes (team-map, office with pay access only).
 */
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { VisitCard } from "@steward/shared";
import { useMapSpots, useMapsKey, useTeamMap } from "../api/hooks";
import { useSession } from "../app/session";
import { clock, plural } from "./format";
import { loadGoogleMaps, onMapsRefused } from "./googleMaps";

// Where every brand works today, for a map with no pins yet.
const HOME_BASE = { lat: 29.9511, lng: -90.0715 };
// Map ID "Steward visit map" in the Steward Cloud project (2026-10-31), with styles "Steward no places" and "Steward no
// places dark" that hide shops, food and other places. Pins drawn as our own HTML need a map ID; the style lives in Google.
export const MAP_ID = "929ac766fd233a404c943482";
// The house from Icon, for the start pin (built by hand, since pins are plain elements Google places).
const HOUSE = "M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4.5v-5.5h-5V20H5a1 1 0 0 1-1-1z";

export interface MapLine {
  color: string;
  points: [number, number][];
  /** Follows the roads: drawn solid. Straight lines between stops stay dashed. */
  roads?: boolean;
}

type Spot = google.maps.LatLngLiteral;

function dark() {
  const t = document.documentElement.dataset.theme;
  return t === "dark" || (t !== "light" && window.matchMedia("(prefers-color-scheme: dark)").matches);
}

export function VisitMap({
  visits,
  active,
  numberOf,
  colorOf,
  lines,
  home = null,
  bare = false,
  live = false,
}: {
  visits: VisitCard[];
  active?: string | null;
  /** The pin's number; the place in the list when not given. */
  numberOf?: (v: VisitCard, i: number) => number;
  colorOf?: (v: VisitCard) => string | undefined;
  lines?: MapLine[];
  /** Where the teams start the day, drawn as a house pin. */
  home?: [number, number] | null;
  /** The phone Home: no zoom or full screen buttons and no "no map spot" line, the map is the page background. */
  bare?: boolean;
  /** Show where I am (blue) and the team on the clock (green). */
  live?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const drawn = useRef<{ pins: google.maps.marker.AdvancedMarkerElement[]; lines: google.maps.Polyline[] }>({
    pins: [],
    lines: [],
  });
  const navigate = useNavigate();
  const { role, can } = useSession();
  const mapsKey = useMapsKey(role === "crew");
  const team = useTeamMap(live && role !== "crew" && can("payroll.read")).data?.spots ?? [];
  const [me, setMe] = useState<Spot | null>(null);
  const livePins = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const key = mapsKey.data?.key ?? null;
  const [ready, setReady] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const findSpots = useMapSpots();
  // Days already asked for, so a day is looked up once while the map is open.
  const asked = useRef(new Set<string>());
  const [spotProblem, setSpotProblem] = useState<string | null>(null);

  const spotOf = (v: VisitCard): Spot | null => (v.lat !== null && v.lng !== null ? { lat: v.lat, lng: v.lng } : null);
  const placed = visits.flatMap((v) => {
    const at = spotOf(v);
    return at ? [{ v, at }] : [];
  });
  const missing = visits.length - placed.length;

  useEffect(() => {
    if (!key || !box.current) return;
    const el = box.current;
    let gone = false;
    // A pinch on the map zooms the map only. Without this, iPhone Safari also zooms the whole page, blowing up the pins
    // and the map tiles with it (2026-11-01).
    const noPageZoom = (e: Event) => e.preventDefault();
    el.addEventListener("gesturestart", noPageZoom);
    const stop = onMapsRefused(() =>
      setProblem("Google turned down the map key. Head office checks it in Settings, Connected services."),
    );
    loadGoogleMaps(key)
      .then(async () => {
        const { Map } = (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary;
        await google.maps.importLibrary("marker");
        if (gone) return;
        map.current = new Map(el, {
          mapId: MAP_ID,
          center: HOME_BASE,
          zoom: 10,
          colorScheme: dark() ? google.maps.ColorScheme.DARK : google.maps.ColorScheme.LIGHT,
          gestureHandling: "cooperative",
          zoomControl: !bare,
          fullscreenControl: !bare,
          keyboardShortcuts: !bare,
          streetViewControl: false,
          mapTypeControl: false,
          clickableIcons: false,
        });
        // Pins shrink when zoomed far out, so a day's stops do not pile into one blob.
        const m = map.current;
        const size = () => {
          const z = m.getZoom() ?? 10;
          el.style.setProperty("--pin-scale", String(z >= 12 ? 1 : z >= 11 ? 0.85 : z >= 9 ? 0.7 : 0.55));
        };
        size();
        m.addListener("zoom_changed", size);
        setReady(true);
      })
      .catch(() => {
        if (!gone) setProblem("The map did not load. Check the internet and refresh the page.");
      });
    return () => {
      gone = true;
      stop();
      el.removeEventListener("gesturestart", noPageZoom);
      map.current = null;
      setReady(false);
    };
  }, [key, bare]);

  // Ask the server for the spots of each day with visits that have an address but no spot.
  const days = [...new Set(visits.filter((v) => !spotOf(v) && v.address).map((v) => v.date))].sort().join(",");
  useEffect(() => {
    // Crew cannot look up spots (office only); their stops with a known spot still show.
    if (!ready || role === "crew") return;
    for (const date of days ? days.split(",") : []) {
      if (asked.current.has(date)) continue;
      asked.current.add(date);
      findSpots.mutateAsync({ date }).then(
        (r) => setSpotProblem(r.problem),
        () => setSpotProblem("The map could not look up some addresses. Refresh the page to try again."),
      );
    }
  }, [ready, days]);
  const finding = findSpots.isPending;

  // placed is rebuilt each render; spots and active say when it really changed.
  const spots =
    placed
      .map(
        ({ v, at }, i) =>
          `${v.ref}:${String(at.lat)},${String(at.lng)}:${v.status}:${String(numberOf?.(v, i) ?? i)}:${colorOf?.(v) ?? ""}`,
      )
      .join("|") +
    JSON.stringify(lines ?? []) +
    JSON.stringify(home);
  // Where the pins are, so a new order or color does not move a map someone has panned.
  const frame =
    placed
      .map(({ v, at }) => `${v.ref}:${String(at.lat)},${String(at.lng)}`)
      .sort()
      .join("|") + JSON.stringify(home);

  useEffect(() => {
    const m = map.current;
    if (!m || !ready) return;
    const was = drawn.current;
    for (const p of was.pins) p.map = null;
    for (const l of was.lines) l.setMap(null);
    const now: typeof was = { pins: [], lines: [] };
    for (const l of lines ?? [])
      if (l.points.length > 1)
        now.lines.push(
          l.roads
            ? new google.maps.Polyline({
                map: m,
                path: l.points.map(([lat, lng]) => ({ lat, lng })),
                strokeColor: l.color,
                strokeOpacity: 0.8,
                strokeWeight: 4,
              })
            : new google.maps.Polyline({
                map: m,
                path: l.points.map(([lat, lng]) => ({ lat, lng })),
                strokeOpacity: 0,
                // Dashes, as the route lines have always been drawn.
                icons: [
                  {
                    icon: { path: "M 0,-1 0,1", strokeColor: l.color, strokeOpacity: 0.7, strokeWeight: 3, scale: 3 },
                    offset: "0",
                    repeat: "12px",
                  },
                ],
              }),
        );
    placed.forEach(({ v, at }, i) => {
      const on = v.ref === active;
      const color = v.status === "done" ? "var(--color-success)" : (colorOf?.(v) ?? v.crew[0]?.color ?? "var(--color-inactive)");
      // Built as text, never HTML, since client names are typed in by people.
      const pin = document.createElement("span");
      pin.className = `map-pin${on ? " active" : ""}`;
      pin.style.setProperty("--pin", color);
      pin.textContent = String(numberOf ? numberOf(v, i) : i + 1);
      const tip = document.createElement("span");
      tip.className = "map-tip";
      const time = document.createElement("b");
      time.textContent = clock(v.startTime);
      tip.append(time, ` ${v.clientName}`);
      pin.append(tip);
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: m,
        position: at,
        content: pin,
        zIndex: on ? 1000 : i,
        gmpClickable: true,
      });
      marker.addEventListener("gmp-click", () =>
        // Crew open their own visit page.
        role === "crew"
          ? void navigate({ to: "/my-day/$ref", params: { ref: v.ref } })
          : void navigate({ to: "/visits/$ref", params: { ref: v.ref } }),
      );
      now.pins.push(marker);
    });
    if (home) {
      const pin = document.createElement("span");
      pin.className = "map-pin home";
      pin.setAttribute("aria-label", "Where the teams start");
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("viewBox", "0 0 24 24");
      svg.setAttribute("aria-hidden", "true");
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", HOUSE);
      svg.append(path);
      const tip = document.createElement("span");
      tip.className = "map-tip";
      tip.textContent = "Where the teams start";
      pin.append(svg, tip);
      now.pins.push(
        new google.maps.marker.AdvancedMarkerElement({
          map: m,
          position: { lat: home[0], lng: home[1] },
          content: pin,
          zIndex: 999,
        }),
      );
    }
    drawn.current = now;
  }, [spots, active, ready]);

  // Where this browser is. The browser asks once and remembers; a no just leaves the blue dot off.
  useEffect(() => {
    if (!live || !("geolocation" in navigator)) return;
    const watch = navigator.geolocation.watchPosition(
      (p) => setMe({ lat: p.coords.latitude, lng: p.coords.longitude }),
      // A slow or lost fix keeps the last dot.
      () => undefined,
      { enableHighAccuracy: true, maximumAge: 60_000 },
    );
    return () => navigator.geolocation.clearWatch(watch);
  }, [live]);

  // The first fix centers the map on me, once, so a map someone moved stays put after.
  const centered = useRef(false);
  useEffect(() => {
    const m = map.current;
    if (!m || !ready || !me || centered.current) return;
    centered.current = true;
    m.setCenter(me);
    m.setZoom(12);
  }, [me, ready]);

  // The live pins on their own, so a new spot every minute does not redraw the visit pins.
  const liveKey = JSON.stringify(me) + team.map((t) => `${t.personId}:${String(t.lat)},${String(t.lng)}:${t.name}`).join("|");
  useEffect(() => {
    const m = map.current;
    for (const p of livePins.current) p.map = null;
    livePins.current = [];
    if (!m || !ready) return;
    for (const t of team) {
      // Built as text, never HTML, since names are typed in by people.
      const pin = document.createElement("span");
      pin.className = "map-pin crew";
      pin.setAttribute("aria-label", t.name);
      pin.textContent = t.name
        .split(" ")
        .map((w) => w.charAt(0))
        .join("")
        .slice(0, 2)
        .toUpperCase();
      const label = document.createElement("span");
      label.className = "map-name";
      label.textContent = t.name;
      pin.append(label);
      livePins.current.push(
        new google.maps.marker.AdvancedMarkerElement({
          map: m,
          position: { lat: t.lat, lng: t.lng },
          content: pin,
          zIndex: 1001,
        }),
      );
    }
    if (me) {
      const dot = document.createElement("span");
      dot.className = "map-me";
      dot.setAttribute("aria-label", "You are here");
      livePins.current.push(new google.maps.marker.AdvancedMarkerElement({ map: m, position: me, content: dot, zIndex: 1002 }));
    }
  }, [liveKey, ready]);

  // Frame the pins when the visits change, not on hover, so a map someone moved stays put.
  useEffect(() => {
    const m = map.current;
    // Once centered on me, new visits do not pull the map away.
    if (!m || !ready || !placed.length || centered.current) return;
    if (placed.length === 1) {
      m.setCenter(placed[0]!.at);
      m.setZoom(14);
      return;
    }
    const b = new google.maps.LatLngBounds();
    for (const p of placed) b.extend(p.at);
    if (home) b.extend({ lat: home[0], lng: home[1] });
    m.fitBounds(b, 32);
    google.maps.event.addListenerOnce(m, "idle", () => {
      if ((m.getZoom() ?? 0) > 14) m.setZoom(14);
    });
  }, [frame, ready]);

  const note = mapsKey.isError
    ? "The map did not load. Refresh the page to try again."
    : mapsKey.data && !key
      ? "The map needs a Google Maps key. Head office adds it in Settings, Connected services."
      : (problem ?? spotProblem);

  return (
    <div className="visit-map">
      <div ref={box} className={`map-box${note && !ready ? " off" : ""}`} role="region" aria-label="Map of the visits" />
      {note ? <p className="map-note">{note}</p> : null}
      {!bare && !note && ready && !placed.length && !finding ? <p className="map-note">No visits with a map spot yet.</p> : null}
      {!bare && !note && ready && placed.length && missing && !finding ? (
        <p className="map-note">{plural(missing, "visit")} not on the map. Google could not find their address.</p>
      ) : null}
    </div>
  );
}
